import type { Breadcrumb, Event } from '@sentry/angular';

/**
 * PII scrubbing for events leaving the browser toward Sentry — a third
 * party outside Vaki's data-processing boundary, same as GA4. This is
 * AC3 from Notion 3bfc94b6-2cc7-81cf-b780-f58ca1259319: blocking, "no se
 * enciende en producción" until it passes.
 *
 * Deliberately the SAME fail-closed philosophy as
 * `libs/shared/util/ga4/src/lib/payment-error-code.mapper.ts`
 * (`mapPaymentErrorCode` / `UNKNOWN_GATEWAY_ERROR_CODE`) — that file is an
 * ALLOWLIST of bounded gateway codes where anything unrecognized
 * collapses to a sentinel rather than passing through. This scrubber
 * applies the identical rule to a different surface: any custom
 * context/extra/tag/breadcrumb key we might ever attach to a Sentry
 * event. A DENYLIST would require remembering to add every new sensitive
 * field as it's introduced — the PaymentUserInfo inventory from this
 * issue's De-risk (email, `document{type,id}` = cédula, phone, address)
 * plus the known landmine (`cardNumber` as a raw FormControl in the dead
 * MercadoPago component, libs/vakers/checkout/ui/payment-methods/src/lib/
 * mercadopago/cc-mercado-pago/cc-mercado-pago.component.ts) is exactly
 * the kind of list that goes stale. An ALLOWLIST doesn't need remembering:
 * a field nobody has reviewed yet is unknown, and unknown fails closed.
 *
 * This is the test AC3 asks for: "existe un test automatizado que falla
 * si alguien agrega un campo sensible al contexto del error" — see
 * pii-scrubber.spec.ts. Adding a new field to a Sentry context does NOT
 * require touching this allowlist to stay safe; it only needs to be added
 * here if it should actually reach Sentry, and that addition is the
 * reviewable moment.
 *
 * The allowlist above only applies to KEYED surfaces (contexts/extra/tags/
 * breadcrumb.data) — a dictionary where every entry has a name we can
 * accept or reject. Four other surfaces are FREE TEXT with no keys to
 * allowlist: `event.message`, `exception.values[].value`, `request.url`,
 * and `breadcrumb.message` (outside the 'console' category, which is
 * dropped outright). QA's Verify send-back on this PR demonstrated all
 * four leaking PII verbatim — e.g. an exception built from a gateway's
 * error text (the exact pattern at
 * libs/vakers/checkout/data-access/src/lib/checkout.service.ts:1136,
 * `throw new Error(`${data?.body?.errorMessage}`)`) would carry whatever
 * the gateway echoed straight to Sentry. There's no allowlist for free
 * text, so these are handled by pattern-based redaction
 * (`redactFreeText`) instead: same fail-closed intent, applied to
 * unstructured strings rather than object keys.
 */

/**
 * Custom context keys explicitly reviewed as safe to send unmasked.
 * Nothing here is PII: gateway/method identifiers, Vaki's own internal
 * keys (not the donor's), and the already-bounded, already-scrubbed
 * error code this codebase produces via `mapPaymentErrorCode`. Amount
 * (`monto`) is intentionally NOT allowlisted — the De-risk explicitly
 * groups it with PII-adjacent data leaving to a third party.
 */
const ALLOWED_CONTEXT_KEYS = new Set<string>([
  'gateway',
  'paymentMethod',
  'errorCode',
  'vakiKey',
  'checkoutStep',
  'currency',
  'httpStatus'
]);

/** Value substituted for anything not on the allowlist. */
export const SCRUBBED_PLACEHOLDER = '[scrubbed:vaki-pii-allowlist]';

/** Value substituted into free text where a PII pattern matched. */
export const SCRUBBED_TEXT_PLACEHOLDER = '[scrubbed:vaki-pii-pattern]';

const MAX_DEPTH = 6;

/** Matches an email address anywhere in a string. */
const EMAIL_PATTERN = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

/**
 * Matches a run of 6+ digits, optionally grouped with spaces, commas,
 * dashes, dots, or parentheses, any of those separators possibly
 * repeated (e.g. "300 123 4567", "123-45-6789", "123,456,789",
 * "123  456  789"). One pattern covers three different kinds of PII on
 * purpose — a Colombian cédula/documento (6-10 digits), a phone number
 * (10-13 digits incl. country code), and a card number (13-19 digits,
 * often grouped in 4s) — rather than three near-identical regexes trying
 * to guess which is which from free text alone. Same fail-closed
 * reasoning as the rest of this file: a short number (an HTTP status, a
 * year, a line number) survives; anything long enough to plausibly
 * identify a person or a card does not. This also catches a raw
 * monetary amount if one lands in free text — consistent with `amount`
 * being deliberately excluded from ALLOWED_CONTEXT_KEYS above.
 *
 * The separator class includes `,` (QA Review send-back #2: a
 * comma-grouped amount like "123,456,789" — the English/USD format used
 * by `apps/foundation/usa` — escaped the original class entirely) and
 * allows 1+ repetitions per gap, not just 0-or-1 (a repeated separator
 * like "123  456  789", double space, otherwise broke the run into
 * fragments too short to match). Re-verified after widening: a Stripe
 * PaymentIntent id (`pi_3PQ...`, alphanumeric — never a pure digit run),
 * a `chunk-<hash>.js` hex hash (mixed letters), and `lineno`/`colno`
 * (typed `number`, never reaches `redactFreeText` which only runs on
 * strings) all still survive — see pii-scrubber.spec.ts.
 */
const LONG_DIGIT_RUN_PATTERN = /(?:\d[\s,().-]*){5,}\d/g;

/**
 * Colombian street addresses, which neither the key allowlist nor the two
 * patterns above can catch when they appear inside free text.
 *
 * Found by a real staging verification on 2026-08-28, not in review: an
 * error built the way `checkout.service.ts:1136` builds one —
 * `throw new Error(`${gateway.errorMessage}`)` — reached Sentry with
 * `addr Calle 100 #15-20 Bogota` in clear while the email, document,
 * phone and amount beside it were all redacted. AC3 requires the address
 * masked too.
 *
 * A street address in prose has no generic shape, so this anchors on the
 * Colombian street-type vocabulary and REQUIRES a digit after it — the
 * thing that makes it an address rather than the word "calle" in a
 * sentence. That keeps it from eating ordinary prose while still covering
 * every form the checkout collects. Deliberately bounded: an address with
 * no street-type word is not matched, which is the same accepted-miss
 * posture as the bot list, not the fail-closed posture of the key
 * allowlist — the allowlist remains the real defense, because in the app
 * an address travels as the `address` FIELD. This pattern only exists for
 * the case where something interpolates it into a message.
 */
const CO_STREET_ADDRESS_PATTERN =
  /\b(?:calles?|cll|carreras?|cra|kra|avenidas?|avda|autopista|diagonal|diag|transversal|trans|manzana|apartamento|apto|torre|bloque)\b\.?\s*#?\s*\d[\w\s#\-.]{0,40}/gi;

/**
 * Redacts PII patterns out of a free-text string. This is the free-text
 * counterpart to `scrubObject`'s key allowlist — there are no keys here
 * to allow or deny, so it redacts by pattern instead. Used on the four
 * surfaces that carry unstructured, developer- or gateway-authored text:
 * `event.message`, `exception.values[].value`, `request.url`, and
 * non-console `breadcrumb.message`.
 */
export function redactFreeText(text: string): string {
  return text
    .replace(EMAIL_PATTERN, SCRUBBED_TEXT_PLACEHOLDER)
    .replace(CO_STREET_ADDRESS_PATTERN, SCRUBBED_TEXT_PLACEHOLDER)
    .replace(LONG_DIGIT_RUN_PATTERN, SCRUBBED_TEXT_PLACEHOLDER);
}

/**
 * Scrubs a URL before it reaches Sentry. Query params are the obvious
 * vector (`?email=...&doc=...`) so the query string and hash are
 * dropped wholesale rather than pattern-matched — today's checkout flow
 * happens not to put PII in query params, but that's a property of
 * today's code, not a guarantee, so the URL is treated as untrusted
 * regardless. `redactFreeText` is then applied to what's left (origin +
 * path) as defense in depth, in case a path segment ever carries PII.
 */
function scrubUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  try {
    const parsed = new URL(url);
    parsed.search = '';
    parsed.hash = '';
    return redactFreeText(parsed.toString());
  } catch {
    // Not a parseable absolute URL — fail closed on the raw string same
    // as everything else here rather than passing it through unchecked.
    return redactFreeText(url);
  }
}

function scrubValue(value: unknown, depth: number): unknown {
  if (depth > MAX_DEPTH) return SCRUBBED_PLACEHOLDER;
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) {
    return value.map((item) => scrubValue(item, depth + 1));
  }
  if (typeof value === 'object') {
    return scrubObject(value as Record<string, unknown>, depth);
  }
  // Primitives (string/number/boolean) at the top of a scrubbed object are
  // handled by the caller via the allowlist — by the time we're recursing
  // into a nested object's values, every key has already been through
  // scrubObject, so any primitive reaching here belongs to an allowed key.
  return value;
}

/**
 * Walks a plain object and keeps only allowlisted keys, scrubbing
 * everything else. Applied to `event.extra`, `event.contexts` (the custom
 * ones this app sets — Sentry's own `browser`/`os`/`runtime`/`culture`/
 * `app` contexts are structural, not user data, and are left alone by the
 * caller), `event.tags`, and breadcrumb `data`.
 */
export function scrubObject(
  input: Record<string, unknown> | undefined,
  depth = 0
): Record<string, unknown> | undefined {
  if (!input) return input;
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input)) {
    if (ALLOWED_CONTEXT_KEYS.has(key)) {
      result[key] = scrubValue(value, depth + 1);
    } else {
      result[key] = SCRUBBED_PLACEHOLDER;
    }
  }
  return result;
}

/**
 * Sentry's own built-in context names — structural diagnostics the SDK
 * itself populates (browser/OS/runtime info), never user-supplied data.
 * The ONLY names exempt from key-level scrubbing. Deliberately an
 * allowlist of what's exempt, not a denylist of what's custom: a future
 * custom context under any other name — 'payment', 'checkout', or one
 * nobody has thought of yet — falls through to `scrubObject` by default.
 * Getting this backwards (denylisting 'payment'/'checkout' by name) is
 * exactly the class of bug this file exists to prevent — see the
 * top-of-file doc comment on why this is an allowlist everywhere.
 */
const SENTRY_BUILTIN_CONTEXT_NAMES = new Set<string>([
  'browser',
  'os',
  'runtime',
  'culture',
  'app',
  'device',
  'cloud_resource',
  'response',
  'trace',
  'state',
  'profile'
]);

function scrubContexts(
  contexts: Event['contexts']
): Event['contexts'] | undefined {
  if (!contexts) return contexts;
  const result: NonNullable<Event['contexts']> = { ...contexts };
  for (const name of Object.keys(result)) {
    if (SENTRY_BUILTIN_CONTEXT_NAMES.has(name)) continue;
    result[name] = scrubObject(
      result[name] as Record<string, unknown> | undefined
    );
  }
  return result;
}

/**
 * Strips anything beyond an internal user id. `sendDefaultPii: false` at
 * init already keeps Sentry from auto-populating this, but a call site
 * setting `Sentry.setUser({ email, ip_address })` by hand is exactly the
 * kind of regression this guards against — same "unknown/unexpected
 * fails closed" rule as the rest of this file.
 */
function scrubUser(user: Event['user']): Event['user'] | undefined {
  if (!user) return user;
  return user.id ? { id: user.id } : undefined;
}

/**
 * Auth/session headers and cookies never belong in an error report;
 * `url` is treated as untrusted free text (see `scrubUrl`).
 *
 * `query_string` and `data` are dropped wholesale too — same fail-closed
 * standard `scrubUrl` already applies to the query string embedded in
 * `url`. This closes the gap QA found (Notion
 * 3bfc94b6-2cc7-81cf-b780-f58ca1259319, Review send-back #2): `@sentry/node-core`'s
 * `requestDataIntegration` is one of `getDefaultIntegrations()` and
 * `sentry-server.init.ts` doesn't filter integrations (unlike the browser
 * side, which explicitly drops `BrowserTracing`), so with
 * `sendDefaultPii: false` at init, `include.query_string` still resolves
 * to `true` — the `dataCollection.urlQueryParams` deny-list only applies
 * to headers, never to `request.query_string`. `data` is the request
 * body (`unknown` per Sentry's own type, e.g. POSTed form/JSON) — no
 * narrower allowlist is safe for it than for headers/cookies, so it gets
 * the same treatment: deleted outright, not pattern-redacted.
 */
function scrubRequest(request: Event['request']): Event['request'] | undefined {
  if (!request) return request;
  const safe = { ...request };
  delete safe.headers;
  delete safe.cookies;
  delete safe.query_string;
  delete safe.data;
  safe.url = scrubUrl(safe.url);
  return safe;
}

/**
 * `exception.values[].value` is the exception message — free text
 * written by a developer or, per the checkout.service.ts:1136 pattern,
 * echoed straight from an external gateway's response body. Structural
 * fields on each value (`type`, `mechanism`, `stacktrace`) are left
 * alone; only the message text is redacted.
 */
function scrubException(
  exception: Event['exception']
): Event['exception'] | undefined {
  if (!exception?.values) return exception;
  return {
    ...exception,
    values: exception.values.map((value) =>
      typeof value.value === 'string'
        ? { ...value, value: redactFreeText(value.value) }
        : value
    )
  };
}

export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  // Console breadcrumbs mirror whatever a developer passed to
  // console.log/warn/error verbatim — e.g. a stray `console.log(paymentUserInfo)`
  // left in during debugging. There's no reliable allowlist for free-form
  // console arguments, so the category is dropped outright rather than
  // partially trusted.
  if (breadcrumb.category === 'console') return null;
  return {
    ...breadcrumb,
    // Non-console categories (xhr/navigation/ui.click/...) still carry a
    // free-text `message` — pattern-redact it same as event.message.
    message:
      typeof breadcrumb.message === 'string'
        ? redactFreeText(breadcrumb.message)
        : breadcrumb.message,
    data: scrubObject(breadcrumb.data)
  };
}

/**
 * `beforeSend` hook — the single choke point every event (error or
 * message) passes through before Sentry.init() hands it to the
 * transport. Purely structural fields (stacktrace shape, sdk metadata,
 * release, environment, level, timestamps, Sentry's own contexts) pass
 * through unchanged. Every surface that can carry user- or
 * gateway-supplied content is scrubbed: keyed surfaces
 * (contexts/extra/tags/breadcrumb.data) via the allowlist, and free-text
 * surfaces (message/exception value/request url/breadcrumb message) via
 * pattern-based redaction.
 */
export function scrubSentryEvent<E extends Event>(event: E): E {
  return {
    ...event,
    message:
      typeof event.message === 'string'
        ? redactFreeText(event.message)
        : event.message,
    exception: scrubException(event.exception),
    user: scrubUser(event.user),
    request: scrubRequest(event.request),
    extra: scrubObject(event.extra),
    tags: event.tags
      ? (scrubObject(event.tags as Record<string, unknown>) as Event['tags'])
      : event.tags,
    contexts: scrubContexts(event.contexts),
    breadcrumbs: event.breadcrumbs
      ?.map(scrubBreadcrumb)
      .filter((b): b is Breadcrumb => b !== null)
  };
}
